import React, { useCallback } from "react";

import { Theme, useMediaQuery } from "@mui/material";

import { Button } from "@/components/form/Button/Button";
import { Paper } from "@/components/paper/Paper";
import { ProfileAvatar } from "@/components/profile-pages/profile-avatar";
import { LadyNameVerified } from "@/components/ui/lady-name-verified/LadyNameVerified";
import { LocalLadyIdContextProvider } from "@/context/local-lady-id-context/LocalLadyId.context";
import { useLadyProfileDto } from "@/features/lady-profile/hooks";
import { SignUpForm } from "@/features/sign-up-form/SignUpForm";
import { InjectOnLoadSpinner } from "@/lib/inject-on-load-spinner/InjectOnLoadSpinner";
import { LadyMinV2DTO } from "@/models/lady-dtos";
import { ImageWithRef } from "@/shared/image-with-ref/ImageWithRef";

import { ModalCloseCross } from "../components/ModalCloseCross";
import { useOpenModal } from "../utils/openModal";

type PropTypes = {
  onClose: () => void;
  header: string;
  ladyDtoParam?: LadyMinV2DTO;
  isOmitHeaderForWord?: boolean;
};

export const ProfileActionButtonsModal: React.FC<PropTypes> = ({
  onClose,
  header,
  ladyDtoParam,
  isOmitHeaderForWord,
}) => {
  const { data: ladyDto } = useLadyProfileDto();
  const { openModal, removeOne } = useOpenModal();
  const isUpMd = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const NameAndCity = useCallback(
    ({ city, name }: { city: string; name: string }) => {
      return (
        <>
          <LadyNameVerified
            className="font-bold text-base mt-1"
            iconHeight={20}
            iconWidth={20}
          >
            {name}
          </LadyNameVerified>
          <span data-testid="ladyLocation" className="text-sm text-grey1-700">
            {city}
          </span>
        </>
      );
    },
    [],
  );

  if (!ladyDtoParam && !ladyDto) return null;

  const openSignInModal = () =>
    openModal("SIGN_IN_FORM_MODAL", {
      onClose: removeOne,
      localLadyId: ladyDtoParam?.id.toString() || ladyDto?.forms.id.toString(),
    });

  const { avatar, name, city } = (() => {
    if (!!ladyDtoParam) {
      return {
        avatar: ladyDtoParam.photoSrcs.medium,
        name: ladyDtoParam.firstName,
        city: ladyDtoParam.from,
      };
    } else if (!!ladyDto) {
      return {
        avatar: ladyDto.avatar?.photoSrcs.medium || "none",
        name: ladyDto.forms.name,
        city: ladyDto.forms.city,
      };
    }

    return {
      avatar: "",
      name: "",
      city: "",
    };
  })();

  return (
    <LocalLadyIdContextProvider ladyId={ladyDtoParam?.id.toString()}>
      <Paper
        data-testid="personalizedSignUpModal"
        className="p-4 py-8 md:p-8 min-h-min relative"
      >
        <ModalCloseCross absolute onClose={onClose} />
        <h2 className="text-[22px] lg:text-2xl text-grey1-800 font-[680] text-center mb-4">
          Join for Free {isOmitHeaderForWord ? "" : "for"}{" "}
          <span className="whitespace-nowrap">{header}</span>
        </h2>
        <div className="flex gap-8">
          <div className="flex flex-col items-center gap-2">
            <div className="flex flex-col items-center md:hidden">
              <ProfileAvatar
                alt={`${name} from ${city} - avatar`}
                src={avatar}
                className="w-20 h-20 rounded-full overflow-clip"
              />
              <NameAndCity city={city} name={name} />
            </div>
            <div className="max-w-[380px] md:max-w-[340px]">
              <SignUpForm
                skipHeader
                skipFirstParagraph={!isUpMd}
                ladyIdRedirectParam={ladyDtoParam?.id.toString()}
              />
            </div>
            <div className="w-full">
              <span className="text-[13px] lg:text-sm">
                Already a member?{" "}
                <Button
                  variant="text"
                  className="text-blue text-[13px] lg:text-sm mb-[3px]"
                  onClick={openSignInModal}
                >
                  Sign in Here
                </Button>
              </span>
            </div>
          </div>
          <div className="hidden md:flex flex-col gap-2">
            <div className="w-[200px] h-[300px] relative rounded-lg overflow-clip">
              <InjectOnLoadSpinner spinnerAbsoluteCenter>
                <ImageWithRef src={avatar} alt="Lady photo" />
              </InjectOnLoadSpinner>
            </div>
            <div className="flex flex-col">
              <NameAndCity city={city} name={name} />
            </div>
          </div>
        </div>
      </Paper>
    </LocalLadyIdContextProvider>
  );
};
